import { useMemo, useState } from "react";
import {
  buildMaintenanceProfile,
  buildMilestones,
  nextServicesDue,
  buildMaintenanceEconomics,
  type MaintenanceContext,
} from "@/lib/maintenance";
import { MaintenanceTimeline } from "@/components/MaintenanceTimeline";
import { MaintenancePdfButton } from "@/components/MaintenancePdfButton";
import { slugifyFilename } from "@/lib/maintenance-pdf";
import { MaintenanceIcsButton } from "@/components/MaintenanceIcsButton";
import { dateForMiles, type IcsReminder } from "@/lib/maintenance-ics";
import {
  MaintenanceLogPanel,
  useMaintenanceLog,
} from "@/components/MaintenanceLog";
import { nextDueWithLog } from "@/lib/maintenance-log";
import { MaintenanceReminderForm } from "@/components/MaintenanceReminderForm";
import { MaintenanceYearPanel } from "@/components/MaintenanceYearPanel";

const miles = (n: number) => `${n.toLocaleString()} mi`;

export function MaintenanceDirectAnswer({
  vehicles,
  fullName,
  ctx = {},
}: {
  vehicles: any[];
  fullName: string;
  ctx?: MaintenanceContext;
}) {
  if (!vehicles.length) return <p>No data available.</p>;
  const p = buildMaintenanceProfile(vehicles, ctx);
  const rotation = p.items.find((i) => i.service === "Tire rotation");
  return (
    <p className="text-lg text-foreground">
      The <strong>{fullName}</strong>{" "}
      {p.isElectric ? (
        <>
          needs <strong className="text-primary">no oil changes</strong> — tire rotation
          every {miles(rotation?.intervalMiles ?? 7500)} and brake fluid every 3 years
          are the main items.
        </>
      ) : (
        <>
          needs an oil &amp; filter change every{" "}
          <strong className="text-primary text-xl">{miles(p.oilIntervalMiles)}</strong> or
          12 months, plus tire rotation every {miles(rotation?.intervalMiles ?? 7500)}.{" "}
          {p.oilReason}
        </>
      )}
    </p>
  );
}



export function MaintenanceSchedule({
  vehicles,
  fullName,
  makeSlug,
  modelSlug,
  availableSpecTypes = [],
  ctx = {},
  byYear = false,
  year = null,
  linkableYears,
}: {
  vehicles: any[];
  fullName: string;
  makeSlug?: string;
  modelSlug?: string;
  availableSpecTypes?: string[];
  ctx?: MaintenanceContext;
  /** Show the per-model-year selector and all-years interval table. */
  byYear?: boolean;
  /** Selected model year from the ?year= search param. */
  year?: number | null;
  /** Years the loader treats as indexable ?year= URLs (everything else 301s). */
  linkableYears?: number[];
}) {
  const profile = useMemo(() => buildMaintenanceProfile(vehicles, ctx), [vehicles, ctx]);
  const milestones = useMemo(() => buildMilestones(profile), [profile]);
  const economics = useMemo(() => buildMaintenanceEconomics(profile, ctx), [profile, ctx]);
  const [odometer, setOdometer] = useState<string>("");
  const [annual, setAnnual] = useState<string>("12000");
  const log = useMaintenanceLog(
    fullName.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
  );

  const odo = Number(odometer.replace(/[^0-9]/g, ""));
  const due =
    odo > 0 ? nextDueWithLog(profile, log.entries, odo).slice(0, 5) : [];
  const annualMiles = Number(annual.replace(/[^0-9]/g, "")) || 12000;

  const buildReminders = (): IcsReminder[] => {
    const base =
      odo > 0 ? nextDueWithLog(profile, log.entries, odo).slice(0, 12) : [];
    if (base.length) {
      return base.map((d) => ({
        title: `${fullName} — ${d.service}`,
        date: dateForMiles(Math.max(d.inMiles, 0), annualMiles),
        description: `Due at ${miles(d.dueAt)} (about ${miles(
          Math.max(d.inMiles, 0),
        )} from your ${miles(odo)} reading, based on ${miles(
          annualMiles,
        )} per year).${
          d.lastDoneOn ? ` Last logged ${d.lastDoneOn}.` : ""
        } Source: AutoSpecData maintenance schedule.`,
      }));
    }
    // No odometer entered — schedule the first occurrence of each interval.
    return profile.items
      .filter((i) => i.intervalMiles > 0)
      .slice(0, 12)
      .map((i) => ({
        title: `${fullName} — ${i.service}`,
        date: dateForMiles(i.intervalMiles, annualMiles),
        description: `Every ${miles(i.intervalMiles)}${
          i.intervalMonths ? ` or ${i.intervalMonths} months` : ""
        }. ${i.note ?? ""}`.trim(),
      }));
  };

  return (
    <div>
      {/* One-click PDF export */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-4 sm:p-5">
        <p className="text-sm text-muted-foreground">
          Printable checklist with tick boxes for every service and mileage milestone.
        </p>
        <MaintenancePdfButton
          label={`Download ${fullName} PDF checklist`}
          build={() => ({
            title: `${fullName} Maintenance Schedule`,
            subtitle: `${profile.powertrain.charAt(0).toUpperCase() + profile.powertrain.slice(1)} — printable service checklist`,
            intro: profile.isElectric
              ? `The ${fullName} needs no oil changes. Tire rotation every 7,500 miles and brake fluid every 3 years are the main recurring items.`
              : `The ${fullName} needs an oil & filter change every ${miles(profile.oilIntervalMiles)} or 12 months, plus tire rotation every 7,500 miles.`,
            filename: `${slugifyFilename(fullName)}-maintenance-checklist.pdf`,
            sections: [
              {
                heading: "Service intervals",
                items: profile.items.map((i) => ({
                  label: `${i.service} — ${
                    i.intervalMiles > 0 ? miles(i.intervalMiles) : "As needed"
                  }${i.intervalMonths ? ` / ${i.intervalMonths} mo` : ""}`,
                  note: i.note,
                })),
              },
              {
                heading: "Mileage milestones",
                items: milestones.map((m) => ({
                  label: miles(m.miles),
                  note: m.services.join(", "),
                })),
              },
            ],
          })}
        />
        <MaintenanceIcsButton
          label="Add reminders to calendar (.ics)"
          build={() => ({
            reminders: buildReminders(),
            calendarName: `${fullName} maintenance`,
            filename: `${slugifyFilename(fullName)}-maintenance-reminders.ics`,
          })}
        />
      </div>

      {byYear && (
        <MaintenanceYearPanel vehicles={vehicles} fullName={fullName} ctx={ctx} year={year} makeSlug={makeSlug} modelSlug={modelSlug} linkableYears={linkableYears} />
      )}

      {/* Interval table */}
      <table className="w-full text-sm">
        <thead className="bg-secondary/50 text-left">
          <tr>
            <th className="px-4 py-3 font-semibold">Service</th>
            <th className="px-4 py-3 font-semibold">Interval</th>
            <th className="px-4 py-3 font-semibold">Category</th>
            <th className="px-4 py-3 font-semibold">Notes</th>
          </tr>
        </thead>
        <tbody>
          {profile.items.map((item) => (
            <tr key={item.service} className="border-t border-border align-top">
              <td className="px-4 py-3 font-medium text-foreground whitespace-nowrap">
                {item.service}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-primary font-medium">
                {item.intervalMiles > 0 ? miles(item.intervalMiles) : "As needed"}
                {item.intervalMonths
                  ? ` / ${item.intervalMonths} mo`
                  : ""}
              </td>
              <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                {item.category}
              </td>
              <td className="px-4 py-3 text-muted-foreground">{item.note}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Per-model service economics, derived from this vehicle's own intervals */}
      <div className="border-t border-border p-4 sm:p-5">
        <h3 className="font-semibold text-foreground mb-1">
          What the {fullName} schedule costs to follow
        </h3>
        <p className="text-sm text-muted-foreground">
          Calculated from this vehicle's own interval list above — not a generic
          average. Following it to 100,000 miles means{" "}
          <strong className="text-foreground">{economics.eventsTo100k} service events</strong>{" "}
          across {economics.serviceCount} distinct items
          {economics.oilChangesTo100k > 0 ? (
            <>
              , of which {economics.oilChangesTo100k} are oil and filter changes at the{" "}
              {miles(profile.oilIntervalMiles)} interval
            </>
          ) : (
            <> — with no oil service at all, because this powertrain has no engine oil</>
          )}
          . At independent-shop rates that works out to roughly{" "}
          <strong className="text-foreground">
            ${economics.costTo100kLow.toLocaleString()}–$
            {economics.costTo100kHigh.toLocaleString()}
          </strong>{" "}
          over 100,000 miles, or about ${economics.costPer10kLow}–$
          {economics.costPer10kHigh} per 10,000 miles driven. The item that comes around
          most often is {economics.shortestIntervalService.toLowerCase()} every{" "}
          {miles(economics.shortestIntervalMiles)}, so that is the visit to build the
          rest of the schedule around.
        </p>
      </div>

      {/* Mileage calculator */}
      <div className="border-t border-border p-4 sm:p-5 bg-secondary/20">
        <h3 className="font-semibold text-foreground mb-1">
          What's due next on my {fullName}?
        </h3>
        <p className="text-sm text-muted-foreground mb-3">
          Enter your current odometer reading to see the next services due, then export
          them as calendar reminders.
        </p>
        <div className="flex flex-wrap gap-3">
          <input
            inputMode="numeric"
            value={odometer}
            onChange={(e) => setOdometer(e.target.value)}
            placeholder="e.g. 62,500"
            aria-label="Current mileage"
            className="w-full sm:w-56 rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-primary"
          />
          <input
            inputMode="numeric"
            value={annual}
            onChange={(e) => setAnnual(e.target.value)}
            placeholder="Miles per year (12,000)"
            aria-label="Miles driven per year"
            className="w-full sm:w-56 rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-primary"
          />
        </div>
        {due.length > 0 && (
          <>
          <ul className="mt-4 space-y-2">
            {due.map((d) => (
              <li key={d.service} className="text-sm text-foreground">
                <span className="font-medium">{d.service}</span>
                <span className="text-muted-foreground">
                  {" "}
                  — due at {miles(d.dueAt)} (
                  {d.overdue
                    ? `overdue by ${miles(Math.abs(d.inMiles))}`
                    : `${miles(d.inMiles)} from now`}
                  , around{" "}
                  {dateForMiles(Math.max(d.inMiles, 0), annualMiles).toLocaleDateString(undefined, {
                    month: "short",
                    year: "numeric",
                  })}
                  )
                  {d.lastDoneOn
                    ? ` · last logged ${d.lastDoneOn}${
                        d.lastDoneAt != null ? ` at ${miles(d.lastDoneAt)}` : ""
                      }`
                    : ""}
                </span>
              </li>
            ))}
          </ul>
          <MaintenanceIcsButton
            className="mt-4"
            label="Add these reminders to my calendar"
            build={() => ({
              reminders: buildReminders(),
              calendarName: `${fullName} maintenance`,
              filename: `${slugifyFilename(fullName)}-maintenance-reminders.ics`,
            })}
          />
          </>
        )}
      </div>

      {/* Email reminder signup */}
      <MaintenanceReminderForm
        vehicleLabel={fullName}
        {...(makeSlug ? { makeSlug } : {})}
        {...(modelSlug ? { modelSlug } : {})}
        odometer={odo > 0 ? odo : 0}
        annualMiles={annualMiles}
        nextService={due[0] ? { service: due[0].service, dueAt: due[0].dueAt } : undefined}
      />

      {/* Interactive milestone timeline */}
      <div className="border-t border-border p-4 sm:p-5">
        <MaintenanceTimeline
          profile={profile}
          milestones={milestones}
          fullName={fullName}
          odometer={odo > 0 ? odo : 0}
          completed={log.completedByMilestone}
          onToggleService={log.toggleMilestoneService}
          {...(makeSlug ? { makeSlug } : {})}
          {...(modelSlug ? { modelSlug } : {})}
          availableSpecTypes={availableSpecTypes}
        />

        <details className="mt-6">
          <summary className="cursor-pointer text-sm font-medium text-foreground">
            View all {fullName} milestones as a list
          </summary>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {milestones.map((m) => (
              <div key={m.miles} className="rounded-md border border-border p-3">
                <p className="font-medium text-primary">{miles(m.miles)}</p>
                <p className="text-sm text-muted-foreground">{m.services.join(", ")}</p>
              </div>
            ))}
          </div>
        </details>
      </div>

      {/* Owner's maintenance log */}
      <MaintenanceLogPanel profile={profile} fullName={fullName} log={log} />
    </div>
  );
}
